import { format, set } from "date-fns";
import getSymbolFromCurrency from "currency-symbol-map";
import numeral from "numeral";
import { intersection } from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useState } from "react";
import { MembershipsType } from "redux/services/membership/types";
import { Roles } from "common";
import { getMimeType as getMimeTypeUtil } from "./mimeType";

dayjs.extend(utc);
dayjs.extend(timezone);

export const FORMATS = {
  AMOUNT: "0,0.00",
  DATE: "yyyy-MM-dd",
  DATE_TIME: "yyyy-MM-dd H:m:s",
  DATE_SLASH: "yyyy/MM/dd",
  "YYYY/MM/DD": "YYYY/MM/DD",
  "YYYY-MM-DD": "YYYY-MM-DD",
  "YYYY-MM-DDTHH:mm:ss": "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DD HH:mm": "YYYY-MM-DD HH:mm",
};

export const getCurrentDate = (dateFormat = FORMATS.DATE) =>
  format(new Date(), dateFormat);

export const getCurrentDateTime = (formatType?: string) =>
  format(new Date(), formatType || FORMATS.DATE_TIME);

export const getCurrentMonthFirstDate = () =>
  format(set(new Date(), { date: 1 }), FORMATS.DATE);

export const formatMoney = (amount = 0, currency = "PHP") => {
  const currencyCode = getSymbolFromCurrency(currency);
  if (amount > 0) {
    return `${currencyCode}${numeral(amount).format(FORMATS.AMOUNT)}`;
  } else {
    return `-${currencyCode}${numeral(Math.abs(amount)).format(
      FORMATS.AMOUNT
    )}`;
  }
};

export const formatInputAmount = (value: string) => {
  return `${numeral(value).format("0,0.00")}`;
};

export const constructFullName = (
  firstName?: string,
  middleName?: string,
  lastName?: string
) => [firstName, middleName, lastName].filter((n) => !!n).join(" ");

export const isPermitted = (userRoles: string[], requiredRoles: string[]) => {
  if (requiredRoles.length === 0) return true;
  return intersection(userRoles, requiredRoles).length > 0;
};

export const dateFormat = (
  val?: string,
  useLocalTimezone = false,
  format = "YYYY-MM-DD hh:mm A"
) => {
  if (!val) return undefined;
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dayJsValue = dayjs(val);

  return (
    useLocalTimezone
      ? dayJsValue.utc(true).local().tz(localTimezone)
      : dayJsValue
  ).format(format);
};

export const numberWithCommas = (x?: number) => {
  if (!x || x === 0) return "0";
  return numeral(x).format("0,0");
};

export const downloadFileURLWithCustomName = (
  url: string,
  documentName: string
) => {
  fetch(url)
    .then((res) => {
      return res.blob();
    })
    .then((blob) => {
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.download = documentName;
      a.href = href;
      a.click();
      a.href = "";
    })
    .catch((err) => console.error(err));
};

export const getRowIndex = (
  pageNumber: number,
  pageSize: number,
  index: number
) => {
  return pageSize * (pageNumber - 1) + index + 1;
};

export const useDebounce = (
  value?: string,
  delay?: number
): string | undefined => {
  const [debouncedValue, setDebouncedValue] = useState<string | undefined>(
    value
  );

  useEffect(() => {
    const handleDebounce = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return (): void => {
      clearTimeout(handleDebounce);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const objectToValueUrlEncoded = (obj: { [key: string]: any }) => {
  let str = [];
  for (let p in obj)
    if (obj.hasOwnProperty(p) && !!obj[p]) {
      str.push(`"${p}":"${obj[p]}"`);
    }
  return str.join("&");
};

export const renderContentTypeAsExtensionFile = (filename: string) => {
  // Normalize to lowercase: SAP HCM generates UPPERCASE .CSV extensions
  const ext = filename.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "csv":
      return "application/csv";
    default:
      return "";
  }
};

export const getOrganisationMembership = (
  data?: MembershipsType[]
): MembershipsType | undefined => {
  // Just return the first membership with organisationId (most flexible)
  return data?.find((item) => item.organisationId);
};

// New utility to get organization data from Redux auth state
export const getOrganizationFromRedux = (authState: any) => {
  return authState?.organisationData || null;
};

// Helper to get organization ID with fallbacks
export const getOrganizationId = (authState: any, membershipData?: MembershipsType[]) => {
  // Primary: use Redux organization data
  const reduxOrgData = getOrganizationFromRedux(authState);
  if (reduxOrgData?.organisationId) {
    return reduxOrgData.organisationId;
  }

  // Fallback: use membership data
  const membershipOrg = getOrganisationMembership(membershipData);
  if (membershipOrg?.organisationId) {
    return membershipOrg.organisationId;
  }

  return null;
};

export const parseJwt = (token: string) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const getDefaultRoute = (token?: string | null) => {
  if (token) {
    const roles = parseJwt(token).roles ?? [];

    const isPaymentOps = roles.includes(Roles.PaymentOps);
    const isBusinessAdmin = roles.includes(Roles.BusinessAdmin);
    const isBusinessApprover = roles.includes(Roles.BusinessApprover);
    const isBusinessAssistant = roles.includes(Roles.BusinessOwner);
    const isBUApprover = roles.includes(Roles.BUApprover);

    switch (true) {
      case isPaymentOps:
        return "/payment-processing-requests";
      case isBusinessAdmin:
        return "/enterprise-users";
      case isBusinessApprover:
        return "/approval-dashboard";
      case isBusinessAssistant:
        return "/payments";
      case isBUApprover:
        return "/approval-dashboard";
      default:
        return "";
    }
  }

  return "";
};

export const renderRecordColor = (records: number): string => {
  if (records === 0 || records === 10) {
    return "red.500";
  }

  return "green.500";
};

export const getFirstChar = (str: string): string => {
  return str.trim().charAt(0);
};

export const safeParseJSON = (
  jsonString: string | undefined
): Record<string, any> => {
  try {
    const parsed = JSON.parse(jsonString ?? "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const getMimeType = getMimeTypeUtil;
